// Inline options manager shown inside the product editor when a product has
// selectable options. Lists the product's options (each with its own pricing,
// managed in OptionEditorDialog) and an "Add option" button. Options are keyed
// by the product id, so this only works once the product row exists — for a
// brand-new product it prompts the admin to save first.
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Button, List, ListItem, ListItemText, Chip, Stack, Alert,
} from '@mui/material';
import { toggleCatalogItemActive } from '../../store/admin-catalog-slice.js';
import { openOptionEditor } from '../../store/option-editor-slice.js';
import { optionsForProduct } from '../../utils/catalog-helpers.js';

const OptionsEditor = ({ productId }) => {
  const dispatch = useDispatch();
  const allOptions = useSelector((state) => state.adminCatalog.data.options);

  if (!productId) {
    return (
      <Alert severity="info">
        Save the product first, then add its selectable options here.
      </Alert>
    );
  }

  const options = optionsForProduct(allOptions, productId);

  const openCreate = () => dispatch(openOptionEditor({ option: null, productId }));
  const openEdit = (option) => dispatch(openOptionEditor({ option, productId }));
  const toggleActive = (option) => dispatch(toggleCatalogItemActive({
    resource: 'options', id: option.id, isActive: !option.is_active,
  }));

  return (
    <Box>
      {options.length === 0 ? (
        <Typography color="text.secondary" variant="body2" sx={{ py: 1 }}>
          No options yet. Add one to give this product selectable sub-choices.
        </Typography>
      ) : (
        <List disablePadding>
          {options.map((option) => (
            <ListItem
              key={option.id}
              disableGutters
              divider
              secondaryAction={(
                <Stack direction="row" spacing={0.5}>
                  <Button size="small" onClick={() => openEdit(option)}>Edit</Button>
                  <Button size="small" color="inherit" onClick={() => toggleActive(option)}>
                    {option.is_active ? 'Deactivate' : 'Activate'}
                  </Button>
                </Stack>
              )}
            >
              <ListItemText
                primary={(
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {option.name}
                    {!option.is_active && <Chip label="Inactive" size="small" />}
                  </Box>
                )}
                secondary={option.short_description || null}
              />
            </ListItem>
          ))}
        </List>
      )}

      <Button variant="outlined" size="small" onClick={openCreate} sx={{ mt: 1.5 }}>
        Add option
      </Button>
    </Box>
  );
};

export default OptionsEditor;
